// C4 — first-run coachmarks.
//
// A dismissible, NON-blocking 3-step banner shown ONCE per user on
// first /cleanup workspace open and ONCE on first in-record Cleanup
// drawer open. The three steps are the Cleanup thesis in plain words:
// "this is what's wrong → here's why → here's the safe action".
//
// Persistence reuses the existing per-user view-preferences store
// (useViewPreference → /api/view-preferences → user_view_preferences,
// already in production) — surface_key "cleanup-coachmarks". No new
// table / migration / KV / localStorage. While the preference is
// loading we render nothing (so it never flickers in then out); once
// the relevant *Seen flag is true it never shows again.
//
// Non-blocking by design: it's a banner with a "Got it" button, not a
// modal — it never traps the user (UX: speed is a feature).

import { useViewPreference } from "../hooks/useViewPreference";
import {
  COACHMARK_BASELINE,
  COACHMARK_SURFACE_KEY,
  type CoachmarkState,
  parseCoachmarkState,
} from "./coachmark-state";

type Variant = "workspace" | "drawer";

const STEPS: Record<Variant, { title: string; body: string }[]> = {
  workspace: [
    {
      title: "1 · What's wrong",
      body: "Each card is one Issue — a duplicate, a missing field, an unmatched payment. Pick a lane, then work the queue top to bottom.",
    },
    {
      title: "2 · Why it was flagged",
      body: "The research pane explains, in plain language, why this was flagged and shows the evidence — you never have to read a raw field diff to decide.",
    },
    {
      title: "3 · The safe action",
      body: "The recommended action is one click and reversible for 60 seconds. Choosing a different action is always allowed — it just asks for a one-line reason.",
    },
  ],
  drawer: [
    {
      title: "1 · This record's Issues",
      body: "Everything Cleanup found on the record you're viewing — the same engine as the central workspace, without leaving the page.",
    },
    {
      title: "2 · Why & what changes",
      body: "Research explains why it was flagged; the decide rail shows the before→after in a sentence before anything commits.",
    },
    {
      title: "3 · Nothing's a dead end",
      body: "Even with zero Issues you can “Flag a problem” — it becomes a tracked issue someone can pick up. Most actions undo for 60 seconds.",
    },
  ],
};

export function CleanupCoachmarks({ variant }: { variant: Variant }) {
  const { value, setValue, isLoading } =
    useViewPreference<CoachmarkState>(
      COACHMARK_SURFACE_KEY,
      COACHMARK_BASELINE,
      parseCoachmarkState,
    );

  const seenKey: keyof CoachmarkState =
    variant === "workspace" ? "workspaceSeen" : "drawerSeen";

  // Don't flash while the preference is resolving; never re-show once
  // dismissed.
  if (isLoading || value[seenKey]) return null;

  const dismiss = () => {
    // Best-effort persist (optimistic inside the hook). If the save
    // fails the hook reverts and it'll show again next session —
    // acceptable for a coachmark; never block the user.
    void setValue({ ...value, [seenKey]: true });
  };

  const steps = STEPS[variant];

  return (
    <div
      role="region"
      aria-label="Cleanup quick start"
      style={{
        border: "1px solid var(--blue)",
        background: "var(--blue-tint)",
        borderRadius: 10,
        padding: 14,
        marginBottom: 14,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 10,
          gap: 12,
        }}
      >
        <div
          style={{
            fontFamily: "var(--heading-font-web)",
            fontWeight: 800,
            fontSize: 12,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            color: "var(--navy)",
          }}
        >
          Quick start — how Cleanup works
        </div>
        <a
          href="/cleanup/help"
          style={{
            fontSize: 12,
            color: "var(--blue)",
            fontWeight: 700,
            whiteSpace: "nowrap",
          }}
        >
          Full glossary →
        </a>
      </div>
      <div
        className="cu-quickstart-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: 10,
        }}
      >
        {steps.map((s) => (
          <div
            key={s.title}
            style={{
              background: "var(--bg-0)",
              border: "1px solid var(--mid-gray)",
              borderRadius: 8,
              padding: "10px 12px",
            }}
          >
            <div
              style={{
                fontSize: 12,
                fontWeight: 800,
                color: "var(--navy)",
                marginBottom: 4,
              }}
            >
              {s.title}
            </div>
            <div
              style={{
                fontSize: 12.5,
                color: "var(--fg-3)",
                lineHeight: 1.45,
              }}
            >
              {s.body}
            </div>
          </div>
        ))}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 12,
        }}
      >
        <button
          onClick={dismiss}
          style={{
            border: "1px solid var(--blue)",
            background: "var(--blue)",
            color: "var(--white)",
            borderRadius: 8,
            padding: "6px 16px",
            fontSize: 13,
            fontWeight: 800,
            cursor: "pointer",
          }}
        >
          Got it
        </button>
      </div>
    </div>
  );
}
